import { faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Loading, Modal, Pagination, TextInput } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import { GET_ITEM_LIMIT } from "constants/constants"
import useCommon from "hooks/useCommon"
import { useMemo, useState } from "react"
import BaseTable, { AutoResizer, ColumnShape } from "react-base-table"
import Select from "react-select"
import {
  AssetSubcategoryRecord,
  useCreateAssetSubcategoryMutation,
  useDeleteAssetSubcategoryMutation,
  useListAssetCategoriesQuery,
  useListAssetSubcategoriesQuery,
  useUpdateAssetSubcategoryMutation,
} from "services/aimsBackend"

export default function AssetSubcategoryTab() {
  const { showSnackBar } = useCommon()

  const { data: categories = [] } = useListAssetCategoriesQuery()
  const { data: items = [], isFetching } = useListAssetSubcategoriesQuery()
  const [createSubcategory, { isLoading: isCreating }] = useCreateAssetSubcategoryMutation()
  const [updateSubcategory, { isLoading: isUpdating }] = useUpdateAssetSubcategoryMutation()
  const [deleteSubcategory, { isLoading: isDeleting }] = useDeleteAssetSubcategoryMutation()

  const categoryOptions = useMemo(() => categories.map(c => ({ value: c.id, label: c.title })), [categories])

  const [search, setSearch] = useState("")
  const [categoryFilter, setCategoryFilter] = useState<{ value: number; label: string } | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(GET_ITEM_LIMIT)

  const [showAddModal, setShowAddModal] = useState(false)
  const [addTitle, setAddTitle] = useState("")
  const [addCategoryId, setAddCategoryId] = useState<number | null>(null)
  const [addError, setAddError] = useState("")
  const [addCategoryError, setAddCategoryError] = useState("")

  const [editItem, setEditItem] = useState<AssetSubcategoryRecord | null>(null)
  const [editTitle, setEditTitle] = useState("")
  const [editCategoryId, setEditCategoryId] = useState<number | null>(null)
  const [editError, setEditError] = useState("")
  const [editCategoryError, setEditCategoryError] = useState("")

  const [deleteTarget, setDeleteTarget] = useState<AssetSubcategoryRecord | null>(null)

  const filteredRows = items.filter(item => {
    const matchesSearch = !search || item.title.toLowerCase().includes(search.toLowerCase())
    const matchesCategory = !categoryFilter || item.category.id === categoryFilter.value
    return matchesSearch && matchesCategory
  })

  const pagedRows = filteredRows.slice(
    pageNumber === 1 ? 0 : (pageNumber - 1) * pageSize,
    (pageNumber - 1) * pageSize + pageSize,
  )

  const formatApiError = (err: any, fallback: string) => {
    const data = err?.data
    if (typeof data === "string") return data
    if (data && typeof data === "object") {
      return Object.entries(data)
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
        .join("; ")
    }
    return fallback
  }

  const handleAdd = async () => {
    let hasError = false
    if (!addTitle.trim()) {
      setAddError("Title is required")
      hasError = true
    }
    if (!addCategoryId) {
      setAddCategoryError("Category is required")
      hasError = true
    }
    if (hasError) return

    try {
      await createSubcategory({ title: addTitle.trim(), category_id: addCategoryId! }).unwrap()
      showSnackBar("Asset subcategory added successfully", "success")
      setAddTitle("")
      setAddCategoryId(null)
      setAddError("")
      setAddCategoryError("")
      setShowAddModal(false)
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to create asset subcategory"), "error")
    }
  }

  const handleEdit = async () => {
    let hasError = false
    if (!editTitle.trim()) {
      setEditError("Title is required")
      hasError = true
    }
    if (!editCategoryId) {
      setEditCategoryError("Category is required")
      hasError = true
    }
    if (hasError || !editItem) return

    try {
      await updateSubcategory({
        id: editItem.id,
        body: { title: editTitle.trim(), category_id: editCategoryId! },
      }).unwrap()
      showSnackBar("Asset subcategory updated successfully", "success")
      setEditItem(null)
      setEditTitle("")
      setEditCategoryId(null)
      setEditError("")
      setEditCategoryError("")
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to update asset subcategory"), "error")
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteSubcategory(deleteTarget.id).unwrap()
      showSnackBar("Asset subcategory deleted successfully", "success")
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to delete asset subcategory"), "error")
    } finally {
      setDeleteTarget(null)
    }
  }

  const columns: Array<ColumnShape<AssetSubcategoryRecord>> = [
    { key: "title", dataKey: "title", title: "Title", width: 300, flexGrow: 1 },
    {
      key: "category",
      title: "Category",
      width: 200,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => rowData.category?.title || "",
    },
    {
      key: "action",
      title: "",
      width: 260,
      cellRenderer: ({ rowData }) => (
        <div className="d-flex">
          <Button
            action={() => {
              setEditItem(rowData)
              setEditTitle(rowData.title)
              setEditCategoryId(rowData.category.id)
              setEditError("")
              setEditCategoryError("")
            }}
            label="Edit"
            icon={faPen}
            className="btn-full blue action-btn"
          />
          <Button
            action={() => setDeleteTarget(rowData)}
            label="Delete"
            icon={faTrash}
            className="btn-full danger action-btn ml-2"
          />
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="mt-3">
        <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
          <Loading active={isFetching || isDeleting} />
          <div style={{ flex: 1, maxWidth: 300 }}>
            <TextInput
              labelText="Search"
              hideLabel
              value={search}
              onChange={e => {
                setSearch(e.target.value)
                setPageNumber(1)
              }}
              placeholder="Search asset subcategories..."
            />
          </div>
          <div style={{ width: 220 }}>
            <Select
              classNamePrefix="wfp--react-select"
              className="wfp--react-select-container"
              options={categoryOptions}
              value={categoryFilter}
              onChange={option => {
                setCategoryFilter(option)
                setPageNumber(1)
              }}
              isClearable
              placeholder="Filter by category"
            />
          </div>
          <Button
            action={() => {
              setAddTitle("")
              setAddCategoryId(null)
              setAddError("")
              setAddCategoryError("")
              setShowAddModal(true)
            }}
            label="Add asset subcategory"
            className="btn-full green"
            icon={faPlus}
          />
        </div>

        <div style={{ height: "100%", minHeight: "60vh", marginTop: 10 }}>
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={pagedRows}
                width={width}
                height={height}
                rowKey="id"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        </div>

        <Pagination
          totalItems={filteredRows.length}
          pageSize={pageSize}
          pageSizes={[10, 20, 30]}
          page={pageNumber}
          onChange={(event: any): void => {
            setPageNumber((event as any).page)
            setPageSize((event as any).pageSize)
          }}
        />
      </div>

      <Modal
        open={showAddModal}
        modalHeading="Add Asset Subcategory"
        primaryButtonText={isCreating ? "Adding..." : "Add"}
        secondaryButtonText="Cancel"
        onRequestClose={() => setShowAddModal(false)}
        onSecondarySubmit={() => setShowAddModal(false)}
        onRequestSubmit={handleAdd}
        primaryButtonDisabled={isCreating}
      >
        <TextInput
          labelText="Title"
          value={addTitle}
          onChange={e => {
            setAddTitle(e.target.value)
            if (addError) setAddError("")
          }}
          placeholder="Enter asset subcategory title"
          invalid={!!addError}
          invalidText={addError}
        />
        <div className="mt-2">
          <div className="wfp--form-item">
            <label className="wfp--label">Category</label>
            <Select
              classNamePrefix="wfp--react-select"
              className="wfp--react-select-container"
              options={categoryOptions}
              value={categoryOptions.find(opt => opt.value === addCategoryId) || null}
              onChange={option => {
                setAddCategoryId(option?.value || null)
                if (addCategoryError) setAddCategoryError("")
              }}
              placeholder="Select category"
              styles={addCategoryError ? { control: base => ({ ...base, borderColor: "#da1e28" }) } : {}}
            />
            {addCategoryError && <div className="wfp--form-requirement">{addCategoryError}</div>}
          </div>
        </div>
      </Modal>

      <Modal
        open={!!editItem}
        modalHeading="Edit Asset Subcategory"
        primaryButtonText={isUpdating ? "Saving..." : "Save"}
        secondaryButtonText="Cancel"
        onRequestClose={() => setEditItem(null)}
        onSecondarySubmit={() => setEditItem(null)}
        onRequestSubmit={handleEdit}
        primaryButtonDisabled={isUpdating}
      >
        <TextInput
          labelText="Title"
          value={editTitle}
          onChange={e => {
            setEditTitle(e.target.value)
            if (editError) setEditError("")
          }}
          placeholder="Enter asset subcategory title"
          invalid={!!editError}
          invalidText={editError}
        />
        <div className="mt-2">
          <div className="wfp--form-item">
            <label className="wfp--label">Category</label>
            <Select
              classNamePrefix="wfp--react-select"
              className="wfp--react-select-container"
              options={categoryOptions}
              value={categoryOptions.find(opt => opt.value === editCategoryId) || null}
              onChange={option => {
                setEditCategoryId(option?.value || null)
                if (editCategoryError) setEditCategoryError("")
              }}
              placeholder="Select category"
              styles={editCategoryError ? { control: base => ({ ...base, borderColor: "#da1e28" }) } : {}}
            />
            {editCategoryError && <div className="wfp--form-requirement">{editCategoryError}</div>}
          </div>
        </div>
      </Modal>

      <DeleteConfirmationModal
        open={!!deleteTarget}
        onRequestClose={() => setDeleteTarget(null)}
        onRequestSubmit={handleDelete}
      >
        Are you sure you want to delete &quot;{deleteTarget?.title}&quot;?
      </DeleteConfirmationModal>
    </>
  )
}
